import { Text } from "@otrium/atoms";
import Link from "next/link";
import { ChevronRight, GreyDotIcon } from "src/icons";
import { theme } from "src/theme";
import { BreadcrumbContainer, BreadcrumbItem } from "./Breadcrumb.styled";
import type { Gender } from "src/types/product-listing";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import type { FontSizeKey, SpacingKey } from "src/types/emotion";
import { decodeHTMLString } from "src/utils/helper/string";
import { Fragment } from "react";

export type BreadcrumbItem = {
  title: string;
  slug?: string;
};

type BreadcrumbProps = {
  data: BreadcrumbItem[];
  gender?: Gender;
  separator?: "arrow" | "circle";
  fontSize?: FontSizeKey;
  color?: `#${string}`;
  gap?: SpacingKey;
};

function normalizeSlug(slug: string): string {
  if (!slug) {
    return "/";
  }

  return slug.startsWith("/") ? slug : `/${slug}`;
}

function buildHref(slug: string, gender?: Gender): string {
  const cleanSlug = normalizeSlug(slug);

  if (!gender || gender === ShopType.All) {
    return cleanSlug;
  }

  if (cleanSlug === `/${gender}` || cleanSlug.startsWith(`/${gender}/`)) {
    return cleanSlug;
  }

  return `/${gender}${cleanSlug}`;
}

function Breadcrumb({
  data,
  gender,
  separator = "arrow",
  color,
  fontSize,
  gap,
}: BreadcrumbProps): JSX.Element | null {
  return data.length > 0 ? (
    <BreadcrumbContainer
      style={
        {
          ...(color ? { "--color": color } : {}),
          ...(gap ? { "--gap": theme.space[gap] } : {}),
        } as React.CSSProperties
      }
    >
      {data.map((item, idx) => {
        const parsedTitle = decodeHTMLString(item.title);

        return (
          <Fragment
            key={`${item.title.toLowerCase().replaceAll(" ", "_")}_${idx}`}
          >
            <BreadcrumbItem
              style={
                {
                  ...(color ? { "--color": color } : {}),
                  ...(fontSize
                    ? { "--fontSize": theme.fontSizes[fontSize] }
                    : {}),
                } as React.CSSProperties
              }
            >
              {item.slug !== undefined ? (
                <Link href={buildHref(item.slug, gender)}>{parsedTitle}</Link>
              ) : (
                <Text>{parsedTitle}</Text>
              )}
            </BreadcrumbItem>
            {idx < data.length - 1 ? (
              separator === "arrow" ? (
                <ChevronRight
                  width={theme.space.space16}
                  height={theme.space.space16}
                />
              ) : (
                <GreyDotIcon
                  width={theme.space.space4}
                  height={theme.space.space4}
                />
              )
            ) : null}
          </Fragment>
        );
      })}
    </BreadcrumbContainer>
  ) : null;
}

export { Breadcrumb };
